import { CoreImageView, CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { ConditionalRender, useBehavior } from '@libs/core';
import { AssetsSvgMap, TWidgetProps } from '@libs/types';
import React, { Activity } from 'react';
import { getSemanticColors, SemanticColor, TSemanticColors } from '../../theme/colors';
import { TReferralsProgressData } from '../../types/data';
import { TextStyle } from '../../types/styles';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import styles from './referralsProgress.module.css';

const getDotColor = (semanticColors: TSemanticColors, completedSteps: number, currentStep: number, state: TReferralsProgressData['state']) => {
    switch (state) {
        case 'completed':
        case 'initial':
            return semanticColors[SemanticColor.IconHighlighted];
        case 'expired':
            return semanticColors[SemanticColor.IconEmphasisedDisabled];
        default:
            return semanticColors[currentStep < completedSteps ? SemanticColor.IconEmphasised : SemanticColor.IconEmphasisedDisabled];
    }
};
const ICON_COLOR: Record<NonNullable<TReferralsProgressData['state']>, SemanticColor> = {
    completed: SemanticColor.IconHighlighted,
    expired: SemanticColor.IconEmphasisedDisabled,
    basic: SemanticColor.IconHighlightedDisabled,
    initial: SemanticColor.IconHighlightedDisabled,
};
const ICON_NAME: Record<NonNullable<TReferralsProgressData['state']>, string> = {
    completed: AssetsSvgMap.ic_chec_alt,
    expired: AssetsSvgMap.ic_cross_alt,
    basic: AssetsSvgMap.ic_cross_alt,
    initial: AssetsSvgMap.ic_clock_alt,
};

export const ReferralsProgress: React.FC<TWidgetProps<TReferralsProgressData>> = ({ widget }) => {
    const state = widget.data?.state ?? 'basic';
    const { earnedAmount = 0, totalAmount = 0, totalSteps = 0 } = widget.data?.value ?? {};
    const { referralTitle, startSubtitle, progressSubtitle } = widget.data?.messages ?? {};
    const { margin } = widget.options ?? {};
    const { isHidden } = useBehavior(widget);
    const semanticColors = getSemanticColors(widget.version);

    const completedSteps = totalSteps > 0 ? Math.floor((earnedAmount / totalAmount) * totalSteps) : 0;
    const hasProgress = earnedAmount > 0;
    const fillPercent = totalSteps > 1 && completedSteps > 0 ? ((completedSteps - 1) / (totalSteps - 1)) * 100 : 0;

    const amountColor = semanticColors[hasProgress ? SemanticColor.TextPrimary : SemanticColor.TextSecondary];

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div className={styles.container} style={{ ...getInlineStyles({ margin }) }} data-test={widget.uid}>
                <div className={styles.header}>
                    {referralTitle && (
                        <CoreTextView
                            text={referralTitle}
                            styleProps={{
                                ...TextStyle.main,
                                main: semanticColors[state === 'expired' ? SemanticColor.TextPrimaryDisabled : SemanticColor.TextPrimary],
                            }}
                        />
                    )}
                    <ConditionalRender condition={!!progressSubtitle && state === 'basic'}>
                        <CoreTextView text={progressSubtitle} styleProps={{ ...TextStyle.main, main: amountColor, textAlign: 'end' }} />
                    </ConditionalRender>
                </div>
                <CoreLayoutView
                    className={styles.progressTrack}
                    uid="progress-track"
                    styleProps={{
                        orientation: ORIENTATION_ENUM.HORIZONTAL,
                        lineX: 'space_between',
                        lineY: 'center',
                        height: 8,
                        borderRadius: 8,
                        overflow: 'visible',
                        backgroundColor: semanticColors[state === 'completed' ? SemanticColor.BgHighlightedMuted : SemanticColor.BgSecondary],
                    }}
                >
                    <ConditionalRender condition={state === 'basic'}>
                        <CoreLayoutView
                            className={styles.progressFill}
                            uid="progress-fill"
                            styleProps={{
                                backgroundColor: semanticColors[SemanticColor.BgEmphasisedMuted],
                                width: `${fillPercent}%`,
                                height: 8,
                                borderRadius: 8,
                            }}
                        />
                    </ConditionalRender>
                    {Array.from({ length: state === 'basic' ? totalSteps : 1 }, (_, i) => (
                        <CoreLayoutView
                            key={i}
                            uid={i < completedSteps ? 'dot-active' : 'dot-inactive'}
                            className={styles.dot}
                            styleProps={{
                                backgroundColor: getDotColor(semanticColors, completedSteps, i, state),
                                width: 8,
                                height: 8,
                                borderRadius: 8,
                                flexShrink: 0,
                            }}
                        />
                    ))}
                    <ConditionalRender condition={state !== 'basic'}>
                        <CoreImageView
                            src={getHref(ICON_NAME[state], widget.version)}
                            defaultIcon={getHref(AssetsSvgMap.ic_x, widget.version)}
                            styleProps={{
                                iconColor: semanticColors[ICON_COLOR[state]],
                                width: 16,
                                height: 16,
                            }}
                        />
                    </ConditionalRender>
                </CoreLayoutView>
                {startSubtitle && (
                    <CoreTextView
                        text={startSubtitle}
                        styleProps={{
                            ...TextStyle.secondary,
                            main: semanticColors[state === 'expired' ? SemanticColor.TextPrimaryDisabled : SemanticColor.TextPrimary],
                        }}
                    />
                )}
            </div>
        </Activity>
    );
};
